import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BookOpen, FilmStrip, MusicNote, Microphone, MicrophoneSlash,
  HandsPraying, ChatsTeardrop, Video, Sparkle, CaretLeft, CaretRight,
  Hash, ListChecks, Newspaper, SmileyWink,
} from "@phosphor-icons/react";

/**
 * QuickShortcutsRail — slide-out strip on the LEFT edge of the screen.
 *
 * The original UX problem: every other surface (Bible, Rooms, Movies,
 * Gospel, Prayer, Sermons, Devotional) lives behind a hamburger menu
 * at the TOP of the page. On a long-scrolled feed this means scrolling
 * back up to switch sections — a real annoyance the user flagged.
 *
 * This component lives at fixed left:0, vertical-center, z above the
 * page content but below modals. Collapsed = a 14px tab with an HW
 * brand-mark hint poking out so you always know it's there. Expanded
 * = a vertical rail of 8 shortcuts + a "MIC" recovery row that's
 * impossible to lose.
 *
 * Why it can't be lost:
 *   * Open / closed state persisted to localStorage but the tab
 *     itself is ALWAYS rendered. Even if the rail is collapsed,
 *     pressing the edge tab brings it back.
 *   * Hiding the mic from the Voice Commander does NOT hide this
 *     rail — the "Show mic" button here is the safety net.
 *   * Auto-collapses when the user navigates to a new page so it
 *     doesn't cover the destination content.
 */
// Shortcuts — alphabetical (Feb 2026). Home stays pinned at index 0
// as the safety-net anchor. Everything below sorts A→Z by label so
// users can find any shortcut by name without learning a custom
// order. Color coding reused from the previous layout (terracotta
// for vibrant entry points, forest for daily devotional rhythm).
const SHORTCUTS = [
  { to: "/",             label: "Home",       Icon: Sparkle,       color: "bg-terracotta", testid: "rail-home" },
  { to: "/comedy",       label: "Comedy",     Icon: SmileyWink,    color: "bg-terracotta", testid: "rail-comedy" },
  { to: "/gospel-music", label: "Gospel",     Icon: MusicNote,     color: "bg-forest",     testid: "rail-gospel" },
  { to: "/movies",       label: "Movies",     Icon: FilmStrip,     color: "bg-forest",     testid: "rail-movies" },
  { to: "/news",         label: "News",       Icon: Newspaper,     color: "bg-terracotta", testid: "rail-news" },
  { to: "/prayer-wall",  label: "Prayer",     Icon: HandsPraying,  color: "bg-forest",     testid: "rail-prayer" },
  { to: "/read",         label: "Read",       Icon: BookOpen,      color: "bg-forest",     testid: "rail-read" },
  { to: "/rooms",        label: "Rooms",      Icon: ChatsTeardrop, color: "bg-forest",     testid: "rail-rooms" },
  { to: "/sermons",      label: "Sermons",    Icon: ListChecks,    color: "bg-forest",     testid: "rail-sermons" },
  { to: "/videos",       label: "Videos",     Icon: Video,         color: "bg-forest",     testid: "rail-videos" },
];

const OPEN_KEY = "hw-shortcut-rail-open";
const MIC_HIDDEN_KEY = "hw-voice-fab-hidden";

export default function QuickShortcutsRail() {
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(OPEN_KEY) === "1"; } catch (_) { return false; }
  });
  const [micHidden, setMicHidden] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(MIC_HIDDEN_KEY) === "1"; } catch (_) { return false; }
  });
  const location = useLocation();

  const setOpenPersist = (next) => {
    setOpen(next);
    try { window.localStorage.setItem(OPEN_KEY, next ? "1" : "0"); } catch (_) {}
  };

  // Listen for the Voice Commander toggling hidden state so the
  // "Show mic" row in the rail stays in sync.
  useEffect(() => {
    const onChange = (e) => {
      setMicHidden(!!(e && e.detail && e.detail.hidden));
    };
    window.addEventListener("hw:voice:hidden-changed", onChange);
    return () => window.removeEventListener("hw:voice:hidden-changed", onChange);
  }, []);

  // External-toggle bridge. The new HW outline logo button in the chip
  // rail flips the same `hw-shortcut-rail-open` localStorage key and
  // dispatches a `storage` event. Listening here means tapping the
  // logo opens this rail without us having to drill props through the
  // entire layout tree.
  useEffect(() => {
    const onStorage = (e) => {
      if (!e || e.key !== OPEN_KEY) return;
      setOpen(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Auto-collapse when the floating mini-player tucks into the LEFT
  // edge so the peek tab isn't visually buried under this rail. The
  // event is fired by the MiniPlayer's `onSnap` callback. We don't
  // persist this collapse — once the user explicitly re-opens us via
  // the HW logo, we stay open.
  useEffect(() => {
    const onMiniTucked = () => setOpen(false);
    window.addEventListener("hw:miniplayer:tucked-left", onMiniTucked);
    return () => window.removeEventListener("hw:miniplayer:tucked-left", onMiniTucked);
  }, []);

  // Auto-collapse whenever a full-screen player modal opens. The rail
  // sits at z-67, the InAppMoviePlayer is z-200, the shadcn players
  // are z-81 — so on top, the modal covers the rail visually, but on
  // some sizes the rail can still grab pointer events from the
  // player's bottom-left buttons. Belt-and-braces: just close.
  useEffect(() => {
    const obs = new MutationObserver(() => {
      const modal = document.querySelector(
        '[data-testid="in-app-player"], [data-testid="in-app-video-player"], [data-testid="audio-visualizer-modal"]'
      );
      if (modal) setOpen(false);
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  // Auto-collapse on ANY scroll. The rail's job is "jump-pad" — once
  // the user has moved on and is reading, the rail should get out of
  // the way so it can't block page scroll arrows / floating UI.
  // Mirrors the YouTube / Instagram side-nav behaviour. User report
  // (Feb 2026): "I can't reach the scroll up and down arrows" — the
  // open rail was covering them.
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => setOpen(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  // Auto-collapse when the user navigates somewhere. We DON'T persist
  // this collapse, just animate it shut so the destination is clear.
  // Persisted state is still whatever the user explicitly chose.
  const prevPath = React.useRef(location.pathname);
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      setOpen(false);
      prevPath.current = location.pathname;
    }
  }, [location.pathname]);

  const showMic = () => {
    try { window.localStorage.setItem(MIC_HIDDEN_KEY, "0"); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("hw:voice:hidden-changed", { detail: { hidden: false } })); } catch (_) {}
    setMicHidden(false);
  };

  return (
    <>
      {/* Edge-tab removed iteration 77 — the chip rail's sketchy HW
          logo button is now the single entry point for this rail.
          That bar is sticky to the top of every page, so the logo is
          always one tap away, and removing the orange chevron tab
          eliminates duplicate UI / visual noise on the left edge. */}

      {/* Transparent backdrop while open — so clicking ANYWHERE outside
          the rail closes it (laptop UX fix where the rail was sitting
          on top of page buttons with no obvious way to dismiss). */}
      {open && (
        <button
          type="button"
          aria-label="Close shortcuts"
          data-testid="rail-backdrop"
          onClick={() => setOpenPersist(false)}
          className="fixed inset-0 z-[66] bg-transparent cursor-default"
        />
      )}

      {/* The rail itself. Translates off-screen when collapsed.
          Position note: anchored to the LEFT edge between top-32
          (below BOTH the page header AND the category chip row —
          previously top-20 still overlapped the "Home / Trending /
          Bible / Black Ar…" chips) and bottom-28 (above the
          BottomNav). Internal scroll keeps long lists confined. */}
      <div
        className={`fixed top-32 bottom-28 md:bottom-32 left-0 z-[67] transition-transform duration-300 flex items-center pointer-events-none ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="shortcut-rail"
        data-open={open ? "true" : "false"}
      >
        <div className="bg-forest text-cream rounded-r-2xl shadow-2xl border border-cream/15 border-l-0 py-3 pl-2 pr-3 flex flex-col gap-1.5 max-h-full overflow-y-auto pointer-events-auto">
          <div className="flex items-center justify-between pl-1 pb-1">
            <div className="text-[8px] tracking-[0.3em] uppercase font-bold text-amber-300">
              Shortcuts
            </div>
            {/* Inline close X — fixes laptop scenario where the
                top-of-page HW logo trigger scrolled off but the
                rail is still open and obstructing page buttons. */}
            <button
              type="button"
              onClick={() => setOpenPersist(false)}
              data-testid="rail-close"
              aria-label="Close shortcuts"
              className="w-6 h-6 grid place-items-center rounded-full text-cream/80 hover:text-cream hover:bg-cream/10 active:scale-90 transition-all"
            >
              <span className="text-base leading-none font-bold">×</span>
            </button>
          </div>
          {SHORTCUTS.map(({ to, label, Icon, testid }) => {
            const active = location.pathname === to ||
                          (to !== "/" && location.pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                onClick={() => setOpenPersist(false)}
                className={`flex items-center gap-2 px-2 py-2 rounded-xl transition-all hover:bg-cream/10 active:scale-95 ${
                  active ? "bg-terracotta text-cream" : "text-cream/90"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "duotone"} />
                <span className="text-xs font-semibold tracking-wide">{label}</span>
              </Link>
            );
          })}
          {/* Mic recovery — only shown when the mic is currently
              hidden, so 99% of the time the rail looks clean. */}
          {micHidden && (
            <button
              type="button"
              onClick={showMic}
              data-testid="rail-show-mic"
              className="mt-2 flex items-center gap-2 px-2 py-2 rounded-xl bg-amber-300 text-ink font-bold transition-all hover:bg-amber-200 active:scale-95"
            >
              <Microphone size={18} weight="fill" />
              <span className="text-xs tracking-wide">Show mic</span>
            </button>
          )}
          {!micHidden && (
            <Link
              to="/settings"
              onClick={() => setOpenPersist(false)}
              data-testid="rail-mic-settings"
              className="mt-1 flex items-center gap-2 px-2 py-1.5 rounded-xl text-cream/60 hover:text-cream hover:bg-cream/10 transition-all"
              title="Voice settings"
            >
              <MicrophoneSlash size={14} weight="regular" />
              <span className="text-[10px] tracking-wide">Voice settings</span>
            </Link>
          )}
        </div>
      </div>
    </>
  );
}
